// ============================================================================
// ⚠️  PHI HARD RULE — DO NOT VIOLATE
// Labelary (api.labelary.com) is a THIRD-PARTY web service. It is used here
// ONLY to render anonymized label-format PREVIEWS built from synthetic sample
// data (see sample-data.ts). NEVER send live label data containing PHI — real
// patient name, DOB, MRN, accession/barcode — to this service. Actual
// printing goes straight to the Zebra printer and MUST NOT route through
// Labelary. (Ports legacy `Alpha-App/src/util/labelPreview/labelaryClient.js`.)
// ============================================================================

// Configurable-by-constant per project convention (prefer-variables rule) —
// not inlined into the fetch call below.
export const LABELARY_BASE_URL = 'https://api.labelary.com'

export const DPI_OPTIONS = [152, 203, 300, 600] as const

export type LabelaryDpi = (typeof DPI_OPTIONS)[number]

export const DPI_TO_DPMM: Record<LabelaryDpi, number> = {
  152: 6,
  203: 8,
  300: 12,
  600: 24,
}

export const DEFAULT_DPI: LabelaryDpi = 203

/** Maps a printer DPI to the Labelary dots-per-mm path segment (defaults to 8 = 203dpi). */
export function dpiToDpmm(dpi: number): number {
  return DPI_TO_DPMM[dpi as LabelaryDpi] ?? DPI_TO_DPMM[DEFAULT_DPI]
}

export type LabelaryErrorCode = 'LABELARY_UNREACHABLE' | 'LABELARY_HTTP'

export class LabelaryError extends Error {
  constructor(
    message: string,
    public readonly code: LabelaryErrorCode,
    public readonly status?: number,
  ) {
    super(message)
    this.name = 'LabelaryError'
  }
}

export interface RenderZplPreviewArgs {
  readonly body: string
  readonly widthIn?: number
  readonly heightIn?: number
  readonly dpi?: number
}

/**
 * Renders a ZPL string to a PNG object URL via Labelary.
 * PHI-SAFE: callers pass ZPL already filled with synthetic sample data only.
 * Callers must revoke the returned object URL (`URL.revokeObjectURL`) when done.
 */
export async function renderZplPreview({
  body,
  widthIn = 4,
  heightIn = 2,
  dpi = DEFAULT_DPI,
}: RenderZplPreviewArgs): Promise<string> {
  const dpmm = dpiToDpmm(dpi)
  const url = `${LABELARY_BASE_URL}/v1/printers/${dpmm}dpmm/labels/${widthIn}x${heightIn}/0/`

  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      // Labelary returns HTTP 415 for the browser's default text/plain body; it
      // accepts the raw ZPL under application/x-www-form-urlencoded (it does not
      // form-decode the body, so the ZPL bytes pass through unchanged).
      headers: { Accept: 'image/png', 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
  } catch {
    // fetch() rejects (vs. resolving non-OK) only when the request never got a
    // response: offline, DNS, or — most likely in a locked-down clinic network —
    // a firewall/proxy blocking api.labelary.com. Surface that distinctly.
    throw new LabelaryError(
      'Could not reach the preview service (api.labelary.com) — check network/firewall.',
      'LABELARY_UNREACHABLE',
    )
  }

  if (!res.ok) {
    throw new LabelaryError(`Labelary preview failed (HTTP ${res.status}).`, 'LABELARY_HTTP', res.status)
  }

  const blob = await res.blob()
  return URL.createObjectURL(blob)
}
